import moment from 'moment';
import { countries, FILTER_CONFIG_NAMES, FILTER_FIELD_TYPE } from './DataConstants';
import {
	changeFilterValueToArrayOfIds,
	getEndDateFieldName,
	getStartDateFieldName,
	getTimeZoneFieldName,
	nullSafeGet,
	nullSafeGetOrElse,
} from './DataAccessUtils';
import {
	decodeUTCAdjustedDate,
	encodeUTCAdjustedDate,
	formatDateForParam,
} from './DataFormatterUtils';
import OWRadioGroup from '../components/ow_radio_group/OWRadioGroup';
import OWStaticSelect from '../components/ow_static_select/OWStaticSelect';
import OWAsyncSelect from '../components/ow_async_select/OWAsyncSelect';
import OWDateRangePicker from '../components/ow_date_range_picker/OWDateRangePicker';
import RegionsLocationsTreeSelect from '../components/regions_tree_select/RegionsLocationsTreeSelect';
import ProblemTypeTreeSelect from '../components/problem_type_tree_select';
import {
	COMPLIANCE_EXPIRY_STATUSES,
	COMPLIANCE_REQUEST_LIFECYCLES,
} from '../types/ComplianceTypes';

// Days-out threshold for the "Expiring soon" bucket used across the compliance
// Documents view.
const EXPIRING_SOON_DAYS = 30;

// Documents carry effectiveDate/expiryDate but no server-side expiryStatus, so the
// Documents tab derives the status client-side: Expired if the expiry date has
// passed, Expiring soon within the next 30 days, otherwise Up to date. Returns
// undefined when there is no expiry date to reason about.
// The pipeline chips render one count per status, so a count query must follow
// every filter EXCEPT the selected statuses — otherwise each chip would only ever
// count its own status and the others would read zero.
export const omitStatusFilters = (allFilters: any): any => {
	const { statuses, ...rest } = allFilters || {};
	return rest;
};

export const deriveComplianceExpiryStatus = (expiryDate?: string): string | undefined => {
	if (!expiryDate) {
		return undefined;
	}
	const expiry = moment(expiryDate);
	if (!expiry.isValid()) {
		return undefined;
	}
	const now = moment();
	if (expiry.isBefore(now)) {
		return COMPLIANCE_EXPIRY_STATUSES.EXPIRED;
	}
	if (expiry.isBefore(moment(now).add(EXPIRING_SOON_DAYS, 'days'))) {
		return COMPLIANCE_EXPIRY_STATUSES.EXPIRING_SOON;
	}
	return COMPLIANCE_EXPIRY_STATUSES.UP_TO_DATE;
};

// AdvancedFilters config for the BUYER Overview tab. Every dimension here is a
// real server-side filter the /request_instances endpoint accepts
// (supplierFacilityId, complianceRequestId, dueByStartDate/dueByEndDate). They
// write into the overview target collection's redux filters, which
// PaginatedReduxTable forwards as query params — so the grid is filtered +
// paginated entirely by the server. (Compliant/Non-Compliant has no server param.
// Country is fixed to US for the first draft — sent as a base param, no UI.)
//
// Each entry also carries the `type`/accessor fields SelectedFilters needs so the
// same config renders removable filter pills below the header.
export const buildBuyerComplianceOverviewFilterConfig = (
	targetCollectionName: string,
	updateFilters: (filters: any, targetCollectionName: string) => void,
	supplierFacilitiesSlice: any,
	fetchSupplierFacilities: (...args: any[]) => any,
	suppliersTargetCollectionName: string,
	complianceRequestsSlice: any,
	fetchComplianceRequests: (...args: any[]) => any,
	requestsTargetCollectionName: string
) => [
	{
		label: 'Supplier',
		fieldName: 'supplierFacilityId',
		component: OWAsyncSelect,
		placeholder: 'Filter by supplier',
		stateSlice: supplierFacilitiesSlice,
		targetCollectionName: suppliersTargetCollectionName,
		fetch: fetchSupplierFacilities,
		fetchMultiple: () => {},
		keyAccessor: (supplierFacility) => supplierFacility.id,
		valueAccessor: (supplierFacility) => supplierFacility.id,
		// renderItem only — AdvancedFilters wraps it in a <Select.Option> for the
		// dropdown, and SelectedFilters falls back to it for the pill label. Do NOT
		// add renderRecord here: for OWAsyncSelect it must return a <Select.Option>,
		// and a bare string silently breaks option rendering (empty dropdown).
		renderItem: (supplierFacility) => supplierFacility.name,
		sortBy: { sort_by: 'name', order: 'ascend' },
		filtersValueAccessor: (filtersStateSlice) =>
			nullSafeGet(`${targetCollectionName}.filters.supplierFacilityId`, filtersStateSlice),
		handleChange: (supplierFacilityId) =>
			updateFilters({ supplierFacilityId }, targetCollectionName),
	},
	{
		label: 'Request',
		fieldName: 'complianceRequestId',
		component: OWAsyncSelect,
		placeholder: 'Filter by compliance request',
		stateSlice: complianceRequestsSlice,
		targetCollectionName: requestsTargetCollectionName,
		fetch: fetchComplianceRequests,
		fetchMultiple: () => {},
		keyAccessor: (complianceRequest) => complianceRequest.id,
		valueAccessor: (complianceRequest) => complianceRequest.id,
		// renderItem only (see Supplier note) — no renderRecord for OWAsyncSelect.
		renderItem: (complianceRequest) => complianceRequest.name,
		sortBy: { sort_by: 'name', order: 'ascend' },
		filtersValueAccessor: (filtersStateSlice) =>
			nullSafeGet(`${targetCollectionName}.filters.complianceRequestId`, filtersStateSlice),
		handleChange: (complianceRequestId) =>
			updateFilters({ complianceRequestId }, targetCollectionName),
	},
	{
		label: 'Due date',
		fieldName: 'dueBy',
		type: FILTER_FIELD_TYPE.DATE_RANGE,
		labelPrefix: 'Due',
		component: OWDateRangePicker,
		// SelectedFilters' DATE_RANGE reader hands the formatted label straight to
		// renderItem, so identity accessors are all it needs for the pill.
		valueAccessor: (value) => value,
		renderItem: (value) => value,
		// dueBy range -> dueByStartDate / dueByEndDate query params (repo-standard
		// MM/DD/yyyy HH:mm:ss via formatDateForParam, same as every other date-range
		// filter). Decoded back to [startMoment, endMoment, tz] for the picker.
		filtersValueAccessor: (filtersStateSlice) => {
			const timeZone = nullSafeGet(
				`${targetCollectionName}.filters.${getTimeZoneFieldName('dueBy')}`,
				filtersStateSlice
			);
			const startDate = decodeUTCAdjustedDate(
				nullSafeGet(
					`${targetCollectionName}.filters.${getStartDateFieldName('dueBy')}`,
					filtersStateSlice
				),
				false,
				timeZone
			);
			const endDate = decodeUTCAdjustedDate(
				nullSafeGet(
					`${targetCollectionName}.filters.${getEndDateFieldName('dueBy')}`,
					filtersStateSlice
				),
				true,
				timeZone
			);
			return [startDate && moment(startDate), endDate && moment(endDate), timeZone];
		},
		handleChange: (value, timeZone) => {
			const startDate = value && value.length > 1 && value[0];
			const endDate = value && value.length > 1 && value[1];
			updateFilters(
				{
					[getStartDateFieldName('dueBy')]: startDate
						? formatDateForParam(encodeUTCAdjustedDate(startDate, false, timeZone))
						: undefined,
					[getEndDateFieldName('dueBy')]: endDate
						? formatDateForParam(encodeUTCAdjustedDate(endDate, true, timeZone))
						: undefined,
					[getTimeZoneFieldName('dueBy')]: ((startDate || endDate) && timeZone) || undefined,
				},
				targetCollectionName
			);
		},
	},
];

// AdvancedFilters config for the THIRD-PARTY SUPPLIER Overview tab. Every dimension
// here is a real server-side filter the /request_instances endpoint accepts
// (buyerCompanyId, linkedComplianceDocumentId, dueByStartDate/dueByEndDate — confirmed
// against ComplianceRequestInstanceDaoImpl, the shared buyer/supplier instance DAO).
// They write into the overview target collection's redux filters, which
// PaginatedReduxTable forwards as query params — so the grid is filtered + paginated
// entirely by the server. (No country filter here — the supplier overview is not
// US-scoped.)
//
// Each entry also carries the `type`/accessor fields SelectedFilters needs so the same
// config renders removable filter pills below the header.
export const buildSupplierComplianceOverviewFilterConfig = (
	targetCollectionName: string,
	updateFilters: (filters: any, targetCollectionName: string) => void,
	// Small fixed list of the supplier's clients, pre-mapped to { value, label }
	// (value === buyerCompanyId). Static, so OWStaticSelect (no fetch); type
	// RADIO_GROUP lets SelectedFilters extract the pill straight from `items`.
	clientItems: Array<{ value: any; label: string }>,
	documentsSlice: any,
	fetchDocuments: (...args: any[]) => any,
	documentsTargetCollectionName: string
) => [
	{
		// buyerCompanyId -> backend does `v.toInt`. Single-select client picker.
		// renderRecord returning a string is fine here — for OWStaticSelect it's the
		// <Select.Option> child, not an async-select option builder.
		label: 'Client',
		fieldName: 'buyerCompanyId',
		component: OWStaticSelect,
		type: FILTER_FIELD_TYPE.RADIO_GROUP,
		placeholder: 'Filter by client',
		items: clientItems,
		keyAccessor: (item) => item.value,
		valueAccessor: (item) => item.value,
		renderRecord: (item) => item.label,
		filtersValueAccessor: (filtersStateSlice) =>
			nullSafeGet(`${targetCollectionName}.filters.buyerCompanyId`, filtersStateSlice),
		handleChange: (buyerCompanyId) => updateFilters({ buyerCompanyId }, targetCollectionName),
	},
	{
		// linkedComplianceDocumentId -> backend does `v.toLong`. Single-select over the
		// supplier's own documents.
		label: 'Document',
		fieldName: 'linkedComplianceDocumentId',
		component: OWAsyncSelect,
		placeholder: 'Filter by document',
		stateSlice: documentsSlice,
		targetCollectionName: documentsTargetCollectionName,
		fetch: fetchDocuments,
		fetchMultiple: () => {},
		keyAccessor: (complianceDocument) => complianceDocument.id,
		valueAccessor: (complianceDocument) => complianceDocument.id,
		// renderItem only — AdvancedFilters wraps it in a <Select.Option> for the
		// dropdown, and SelectedFilters falls back to it for the pill label. Do NOT
		// add renderRecord returning a string for OWAsyncSelect (empty dropdown).
		renderItem: (complianceDocument) => complianceDocument.name,
		sortBy: { sort_by: 'name', order: 'ascend' },
		filtersValueAccessor: (filtersStateSlice) =>
			nullSafeGet(`${targetCollectionName}.filters.linkedComplianceDocumentId`, filtersStateSlice),
		handleChange: (linkedComplianceDocumentId) =>
			updateFilters({ linkedComplianceDocumentId }, targetCollectionName),
	},
	{
		label: 'Due date',
		fieldName: 'dueBy',
		type: FILTER_FIELD_TYPE.DATE_RANGE,
		labelPrefix: 'Due',
		component: OWDateRangePicker,
		// SelectedFilters' DATE_RANGE reader hands the formatted label straight to
		// renderItem, so identity accessors are all it needs for the pill.
		valueAccessor: (value) => value,
		renderItem: (value) => value,
		// dueBy range -> dueByStartDate / dueByEndDate query params (repo-standard
		// MM/DD/yyyy HH:mm:ss via formatDateForParam, same as every other date-range
		// filter). Decoded back to [startMoment, endMoment, tz] for the picker.
		filtersValueAccessor: (filtersStateSlice) => {
			const timeZone = nullSafeGet(
				`${targetCollectionName}.filters.${getTimeZoneFieldName('dueBy')}`,
				filtersStateSlice
			);
			const startDate = decodeUTCAdjustedDate(
				nullSafeGet(
					`${targetCollectionName}.filters.${getStartDateFieldName('dueBy')}`,
					filtersStateSlice
				),
				false,
				timeZone
			);
			const endDate = decodeUTCAdjustedDate(
				nullSafeGet(
					`${targetCollectionName}.filters.${getEndDateFieldName('dueBy')}`,
					filtersStateSlice
				),
				true,
				timeZone
			);
			return [startDate && moment(startDate), endDate && moment(endDate), timeZone];
		},
		handleChange: (value, timeZone) => {
			const startDate = value && value.length > 1 && value[0];
			const endDate = value && value.length > 1 && value[1];
			updateFilters(
				{
					[getStartDateFieldName('dueBy')]: startDate
						? formatDateForParam(encodeUTCAdjustedDate(startDate, false, timeZone))
						: undefined,
					[getEndDateFieldName('dueBy')]: endDate
						? formatDateForParam(encodeUTCAdjustedDate(endDate, true, timeZone))
						: undefined,
					[getTimeZoneFieldName('dueBy')]: ((startDate || endDate) && timeZone) || undefined,
				},
				targetCollectionName
			);
		},
	},
];

// AdvancedFilters config for the THIRD-PARTY SUPPLIER Requests tab: Client
// (buyerCompanyId) + Due date, both server-side /request_instances filters. Search is
// AdvancedFilters' built-in input. (No Document filter here — that lives on the
// Overview.) Mirrors the supplier Overview entries; carries the SelectedFilters
// `type`/accessor fields for the pills.
export const buildSupplierComplianceRequestsFilterConfig = (
	targetCollectionName: string,
	updateFilters: (filters: any, targetCollectionName: string) => void,
	clientItems: Array<{ value: any; label: string }>
) => [
	{
		label: 'Client',
		fieldName: 'buyerCompanyId',
		component: OWStaticSelect,
		type: FILTER_FIELD_TYPE.RADIO_GROUP,
		placeholder: 'Filter by client',
		items: clientItems,
		keyAccessor: (item) => item.value,
		valueAccessor: (item) => item.value,
		renderRecord: (item) => item.label,
		filtersValueAccessor: (filtersStateSlice) =>
			nullSafeGet(`${targetCollectionName}.filters.buyerCompanyId`, filtersStateSlice),
		handleChange: (buyerCompanyId) => updateFilters({ buyerCompanyId }, targetCollectionName),
	},
	{
		label: 'Due date',
		fieldName: 'dueBy',
		type: FILTER_FIELD_TYPE.DATE_RANGE,
		labelPrefix: 'Due',
		component: OWDateRangePicker,
		valueAccessor: (value) => value,
		renderItem: (value) => value,
		filtersValueAccessor: (filtersStateSlice) => {
			const timeZone = nullSafeGet(
				`${targetCollectionName}.filters.${getTimeZoneFieldName('dueBy')}`,
				filtersStateSlice
			);
			const startDate = decodeUTCAdjustedDate(
				nullSafeGet(
					`${targetCollectionName}.filters.${getStartDateFieldName('dueBy')}`,
					filtersStateSlice
				),
				false,
				timeZone
			);
			const endDate = decodeUTCAdjustedDate(
				nullSafeGet(
					`${targetCollectionName}.filters.${getEndDateFieldName('dueBy')}`,
					filtersStateSlice
				),
				true,
				timeZone
			);
			return [startDate && moment(startDate), endDate && moment(endDate), timeZone];
		},
		handleChange: (value, timeZone) => {
			const startDate = value && value.length > 1 && value[0];
			const endDate = value && value.length > 1 && value[1];
			updateFilters(
				{
					[getStartDateFieldName('dueBy')]: startDate
						? formatDateForParam(encodeUTCAdjustedDate(startDate, false, timeZone))
						: undefined,
					[getEndDateFieldName('dueBy')]: endDate
						? formatDateForParam(encodeUTCAdjustedDate(endDate, true, timeZone))
						: undefined,
					[getTimeZoneFieldName('dueBy')]: ((startDate || endDate) && timeZone) || undefined,
				},
				targetCollectionName
			);
		},
	},
];

// AdvancedFilters config for the THIRD-PARTY SUPPLIER Documents tab. Both dimensions
// are real server-side filters the /documents list accepts (complianceTypeId, and the
// expiry range expiryStartDate/expiryEndDate — confirmed against ComplianceDocumentDaoImpl).
// They write into the documents target collection's redux filters, which
// PaginatedReduxTable forwards as query params. Search is AdvancedFilters' built-in
// input (server-side name/fileName match). Each entry also carries the `type`/accessor
// fields SelectedFilters needs so the same config renders removable filter pills.
export const buildSupplierComplianceDocumentsFilterConfig = (
	targetCollectionName: string,
	updateFilters: (filters: any, targetCollectionName: string) => void,
	complianceTypesSlice: any,
	fetchComplianceTypes: (...args: any[]) => any,
	typesTargetCollectionName: string
) => [
	{
		label: 'Compliance type',
		fieldName: 'complianceTypeId',
		component: OWAsyncSelect,
		placeholder: 'W9, Certificate of Insurance, License...',
		stateSlice: complianceTypesSlice,
		targetCollectionName: typesTargetCollectionName,
		fetch: fetchComplianceTypes,
		fetchMultiple: () => {},
		keyAccessor: (complianceType) => complianceType.id,
		valueAccessor: (complianceType) => complianceType.id,
		// renderItem only — AdvancedFilters wraps it in a <Select.Option> for the
		// dropdown, and SelectedFilters falls back to it for the pill label. Do NOT
		// add renderRecord returning a string for OWAsyncSelect (empty dropdown).
		renderItem: (complianceType) => complianceType.name,
		sortBy: { sort_by: 'name', order: 'ascend' },
		filtersValueAccessor: (filtersStateSlice) =>
			nullSafeGet(`${targetCollectionName}.filters.complianceTypeId`, filtersStateSlice),
		handleChange: (complianceTypeId) => updateFilters({ complianceTypeId }, targetCollectionName),
	},
	{
		// expiry range -> expiryStartDate / expiryEndDate query params
		// (getStartDateFieldName('expiry') === 'expiryStartDate',
		// getEndDateFieldName('expiry') === 'expiryEndDate'). Same DATE_RANGE machinery
		// as the Due-date filter; the extra expiryTimeZone key the backend ignores.
		label: 'Expiry date',
		fieldName: 'expiry',
		type: FILTER_FIELD_TYPE.DATE_RANGE,
		labelPrefix: 'Expiry',
		component: OWDateRangePicker,
		valueAccessor: (value) => value,
		renderItem: (value) => value,
		filtersValueAccessor: (filtersStateSlice) => {
			const timeZone = nullSafeGet(
				`${targetCollectionName}.filters.${getTimeZoneFieldName('expiry')}`,
				filtersStateSlice
			);
			const startDate = decodeUTCAdjustedDate(
				nullSafeGet(
					`${targetCollectionName}.filters.${getStartDateFieldName('expiry')}`,
					filtersStateSlice
				),
				false,
				timeZone
			);
			const endDate = decodeUTCAdjustedDate(
				nullSafeGet(
					`${targetCollectionName}.filters.${getEndDateFieldName('expiry')}`,
					filtersStateSlice
				),
				true,
				timeZone
			);
			return [startDate && moment(startDate), endDate && moment(endDate), timeZone];
		},
		handleChange: (value, timeZone) => {
			const startDate = value && value.length > 1 && value[0];
			const endDate = value && value.length > 1 && value[1];
			updateFilters(
				{
					[getStartDateFieldName('expiry')]: startDate
						? formatDateForParam(encodeUTCAdjustedDate(startDate, false, timeZone))
						: undefined,
					[getEndDateFieldName('expiry')]: endDate
						? formatDateForParam(encodeUTCAdjustedDate(endDate, true, timeZone))
						: undefined,
					[getTimeZoneFieldName('expiry')]: ((startDate || endDate) && timeZone) || undefined,
				},
				targetCollectionName
			);
		},
	},
];

// AdvancedFilters config for the buyer Requests tab. Every dimension here is a real
// server-side filter the /requests list accepts (complianceTypeId, status,
// isMandatory, locationIds, problemTypeIds, dueByStartDate/dueByEndDate — confirmed
// against ComplianceRequestDaoImpl). They write into the Requests target
// collection's redux filters, which PaginatedReduxTable forwards as query params so
// the grid is filtered + paginated entirely by the server. (Country is fixed to US,
// sent as a base param with no UI.)
//
// Each entry also carries the `type`/accessor fields SelectedFilters needs so the
// same config renders removable filter pills below the header.
export const buildComplianceRequestsFilterConfig = (
	targetCollectionName: string,
	updateFilters: (filters: any, targetCollectionName: string) => void,
	complianceTypesSlice: any,
	fetchComplianceTypes: (...args: any[]) => any,
	typesTargetCollectionName: string,
	locationsSlice: any,
	fetchMultipleLocations: (...args: any[]) => any,
	locationsTargetCollectionName: string,
	problemTypesSlice: any,
	fetchProblemTypes: (...args: any[]) => any,
	fetchMultipleProblemTypes: (...args: any[]) => any,
	problemTypesTargetCollectionName: string
) => [
	{
		label: 'Compliance type',
		fieldName: 'complianceTypeId',
		component: OWAsyncSelect,
		placeholder: 'W9, Certificate of Insurance, License...',
		stateSlice: complianceTypesSlice,
		targetCollectionName: typesTargetCollectionName,
		fetch: fetchComplianceTypes,
		fetchMultiple: () => {},
		keyAccessor: (complianceType) => complianceType.id,
		valueAccessor: (complianceType) => complianceType.id,
		// renderItem only — AdvancedFilters wraps it in a <Select.Option> for the
		// dropdown, and SelectedFilters falls back to it for the pill label. Do NOT
		// add renderRecord returning a string for OWAsyncSelect (empty dropdown).
		renderItem: (complianceType) => complianceType.name,
		sortBy: { sort_by: 'name', order: 'ascend' },
		filtersValueAccessor: (filtersStateSlice) =>
			nullSafeGet(`${targetCollectionName}.filters.complianceTypeId`, filtersStateSlice),
		handleChange: (complianceTypeId) => updateFilters({ complianceTypeId }, targetCollectionName),
	},
	{
		// status -> exact `status` string param (Draft | Published). Static list, so
		// OWStaticSelect (no fetch). type RADIO_GROUP lets SelectedFilters extract the
		// pill from `items`; renderRecord returning a string is fine here (it's the
		// <Select.Option> child, not an async-select option builder).
		label: 'Status',
		fieldName: 'status',
		component: OWStaticSelect,
		type: FILTER_FIELD_TYPE.RADIO_GROUP,
		placeholder: 'Draft or Published',
		items: [
			{ label: 'Draft', value: COMPLIANCE_REQUEST_LIFECYCLES.DRAFT },
			{ label: 'Published', value: COMPLIANCE_REQUEST_LIFECYCLES.PUBLISHED },
		],
		keyAccessor: (item) => item.value,
		valueAccessor: (item) => item.value,
		renderRecord: (item) => item.label,
		filtersValueAccessor: (filtersStateSlice) =>
			nullSafeGet(`${targetCollectionName}.filters.status`, filtersStateSlice),
		handleChange: (status) => updateFilters({ status }, targetCollectionName),
	},
	{
		// isMandatory -> backend does `v.toBoolean`, so send the strings 'true'/'false'.
		// 'All' clears it. type RADIO_GROUP for the SelectedFilters pill.
		label: 'Requirement',
		fieldName: 'isMandatory',
		component: OWRadioGroup,
		type: FILTER_FIELD_TYPE.RADIO_GROUP,
		items: [
			{ label: 'All', value: undefined },
			{ label: 'Mandatory', value: 'true' },
			{ label: 'Optional', value: 'false' },
		],
		keyAccessor: (item) => item.value,
		valueAccessor: (item) => item.value,
		renderRecord: (item) => item.label,
		filtersValueAccessor: (filtersStateSlice) =>
			nullSafeGet(`${targetCollectionName}.filters.isMandatory`, filtersStateSlice),
		handleChange: (isMandatory) => updateFilters({ isMandatory }, targetCollectionName),
	},
	{
		// locationIds -> comma-separated Long ids (backend any-overlap; allLocations
		// rows always match). Uses the same tree-select as the New Request modal.
		// FILTER_CONFIG_NAMES.LOCATION (=== 'locationIds') puts it in
		// multiValueFilterFieldNames so SelectedFilters hydrates the pills itself.
		label: 'Location',
		fieldName: FILTER_CONFIG_NAMES.LOCATION,
		component: RegionsLocationsTreeSelect,
		mode: 'multiple',
		placeholder: 'Filter by location',
		stateSlice: locationsSlice,
		targetCollectionName: locationsTargetCollectionName,
		fetchMultiple: fetchMultipleLocations,
		valueAccessor: (location) => location.id,
		renderItem: (location) => location.name,
		filtersValueAccessor: (filtersStateSlice) =>
			changeFilterValueToArrayOfIds(
				nullSafeGet(`${targetCollectionName}.filters.locationIds`, filtersStateSlice)
			),
		handleChange: (ids) =>
			updateFilters(
				{ locationIds: ids && ids.length > 0 ? ids.join(',') : undefined },
				targetCollectionName
			),
	},
	{
		// problemTypeIds -> comma-separated Int ids. Same hierarchical tree-select as
		// the New Request modal. FILTER_CONFIG_NAMES.PROBLEM_TYPE (=== 'problemTypeIds')
		// is in multiValueFilterFieldNames so SelectedFilters hydrates the pills.
		label: 'Problem type',
		fieldName: FILTER_CONFIG_NAMES.PROBLEM_TYPE,
		component: ProblemTypeTreeSelect,
		mode: 'multiple',
		placeholder: 'Filter by problem type',
		stateSlice: problemTypesSlice,
		targetCollectionName: problemTypesTargetCollectionName,
		fetch: fetchProblemTypes,
		fetchMultiple: fetchMultipleProblemTypes,
		valueAccessor: (problemType) => problemType.id,
		renderItem: (problemType) => problemType.name,
		sortBy: { sort_by: 'name', order: 'ascend' },
		filtersValueAccessor: (filtersStateSlice) =>
			changeFilterValueToArrayOfIds(
				nullSafeGet(`${targetCollectionName}.filters.problemTypeIds`, filtersStateSlice)
			),
		handleChange: (ids) =>
			updateFilters(
				{ problemTypeIds: ids && ids.length > 0 ? ids.join(',') : undefined },
				targetCollectionName
			),
	},
	{
		label: 'Due date',
		fieldName: 'dueBy',
		type: FILTER_FIELD_TYPE.DATE_RANGE,
		labelPrefix: 'Due',
		component: OWDateRangePicker,
		// SelectedFilters' DATE_RANGE reader hands the formatted label straight to
		// renderItem, so identity accessors are all it needs for the pill.
		valueAccessor: (value) => value,
		renderItem: (value) => value,
		// dueBy range -> dueByStartDate / dueByEndDate query params (repo-standard
		// MM/DD/yyyy HH:mm:ss via formatDateForParam). Decoded back to
		// [startMoment, endMoment, tz] for the picker. Mirrors the Overview config.
		filtersValueAccessor: (filtersStateSlice) => {
			const timeZone = nullSafeGet(
				`${targetCollectionName}.filters.${getTimeZoneFieldName('dueBy')}`,
				filtersStateSlice
			);
			const startDate = decodeUTCAdjustedDate(
				nullSafeGet(
					`${targetCollectionName}.filters.${getStartDateFieldName('dueBy')}`,
					filtersStateSlice
				),
				false,
				timeZone
			);
			const endDate = decodeUTCAdjustedDate(
				nullSafeGet(
					`${targetCollectionName}.filters.${getEndDateFieldName('dueBy')}`,
					filtersStateSlice
				),
				true,
				timeZone
			);
			return [startDate && moment(startDate), endDate && moment(endDate), timeZone];
		},
		handleChange: (value, timeZone) => {
			const startDate = value && value.length > 1 && value[0];
			const endDate = value && value.length > 1 && value[1];
			updateFilters(
				{
					[getStartDateFieldName('dueBy')]: startDate
						? formatDateForParam(encodeUTCAdjustedDate(startDate, false, timeZone))
						: undefined,
					[getEndDateFieldName('dueBy')]: endDate
						? formatDateForParam(encodeUTCAdjustedDate(endDate, true, timeZone))
						: undefined,
					[getTimeZoneFieldName('dueBy')]: ((startDate || endDate) && timeZone) || undefined,
				},
				targetCollectionName
			);
		},
	},
];

export const getCountryNameFromCode = (countryCode: string): string => {
	const match = countries.find((country) => country.code === countryCode);
	return match ? match.name : countryCode;
};

// Groups a flat list of records carrying a countryCode into ordered
// [countryName, records[]] tuples for the grouped tables/lists in the
// Compliance Suite (buyer requests, supplier documents, overviews).
export const groupRecordsByCountry = <T extends { countryCode: string }>(
	records: T[]
): Array<{ countryCode: string; countryName: string; records: T[] }> => {
	const groupsByCode: { [code: string]: T[] } = {};
	records.forEach((record) => {
		const code = record.countryCode || 'unknown';
		if (!groupsByCode[code]) {
			groupsByCode[code] = [];
		}
		groupsByCode[code].push(record);
	});
	return Object.keys(groupsByCode)
		.sort()
		.map((code) => ({
			countryCode: code,
			countryName: code === 'unknown' ? 'Other' : getCountryNameFromCode(code),
			records: groupsByCode[code],
		}));
};
